import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Building2 } from "lucide-react";

const EMPTY_FORM = { id: null, nombre: "", codigo: "", contacto_coordinador: "" };

export default function Facultades() {
  const { t } = useTranslation();
  const [facultades, setFacultades] = useState([]);
  const [stats, setStats] = useState({ users: {}, convenios: {} });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([
      api.get("/facultades"),
      api.get("/users"),
      api.get("/convenios"),
    ])
      .then(([f, u, c]) => {
        setFacultades(f.data);
        const userStats = {};
        const convStats = {};
        u.data.forEach((usr) => {
          if (usr.facultad_id) userStats[usr.facultad_id] = (userStats[usr.facultad_id] || 0) + 1;
        });
        c.data.forEach((conv) => {
          (conv.facultad_ids || []).forEach((fid) => {
            convStats[fid] = (convStats[fid] || 0) + 1;
          });
        });
        setStats({ users: userStats, convenios: convStats });
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (f) => {
    setForm({
      id: f.id,
      nombre: f.nombre,
      codigo: f.codigo,
      contacto_coordinador: f.contacto_coordinador || "",
    });
    setShowModal(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        nombre: form.nombre,
        codigo: form.codigo,
        contacto_coordinador: form.contacto_coordinador || null,
      };
      if (form.id) {
        await api.patch(`/facultades/${form.id}`, payload);
        toast.success(t("fac.toast_updated"));
      } else {
        await api.post("/facultades", payload);
        toast.success(t("fac.toast_created"));
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_save"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (f) => {
    const users = stats.users[f.id] || 0;
    const convs = stats.convenios[f.id] || 0;
    if (users > 0 || convs > 0) {
      return toast.error(t("fac.cannot_delete", { users, convs }));
    }
    if (!confirm(t("fac.confirm_delete", { name: f.nombre }))) return;
    try {
      await api.delete(`/facultades/${f.id}`);
      toast.success(t("fac.toast_deleted"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("toast.error_delete"));
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 sm:mb-10">
          <div>
            <div className="section-marker">{t("common.administration")}</div>
            <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
              {t("fac.page_title")}<span className="serif-em text-zinc-900">.</span>
            </h1>
          </div>
          <button onClick={openCreate} className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 w-full sm:w-auto" data-testid="btn-new-facultad">
            <Plus size={18} strokeWidth={1.5} /> {t("fac.btn_new")}
          </button>
        </div>

        {loading ? (
          <div className="text-zinc-500">{t("common.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="facultades-grid">
            {facultades.map((f) => {
              const users = stats.users[f.id] || 0;
              const convs = stats.convenios[f.id] || 0;
              const canDelete = users === 0 && convs === 0;
              return (
                <div key={f.id} className="border border-zinc-200 p-6 flex flex-col" data-testid={`facultad-${f.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="section-marker">{f.codigo}</div>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(f)} className="text-zinc-400 hover:text-zinc-950 p-1" data-testid={`fac-edit-${f.id}`}>
                        <Pencil size={14} strokeWidth={1.5} />
                      </button>
                      <button onClick={() => remove(f)} disabled={!canDelete} className="text-zinc-400 hover:text-red-700 p-1 disabled:opacity-20 disabled:cursor-not-allowed" data-testid={`fac-del-${f.id}`}>
                        <Trash2 size={14} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <h3 className="text-2xl font-medium tracking-tight mb-3 flex-1">{f.nombre}</h3>
                  {f.contacto_coordinador && (
                    <div className="text-xs text-zinc-500 mb-4 truncate" title={f.contacto_coordinador}>
                      {f.contacto_coordinador}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-zinc-100">
                    <div>
                      <div className="kpi-number text-3xl">{users}</div>
                      <div className="section-marker mt-1">{t("fac.users")}</div>
                    </div>
                    <div>
                      <div className="kpi-number text-3xl">{convs}</div>
                      <div className="section-marker mt-1">{t("fac.convenios")}</div>
                    </div>
                  </div>
                </div>
              );
            })}
            {facultades.length === 0 && (
              <div className="col-span-full text-center py-16 border border-zinc-200">
                <Building2 size={36} strokeWidth={1} className="mx-auto text-zinc-400 mb-3" />
                <p className="text-zinc-500">{t("fac.no_data")}</p>
              </div>
            )}
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
            <div className="bg-white w-full max-w-lg p-6 sm:p-8 border border-zinc-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="facultad-modal">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="section-marker">{form.id ? t("fac.modal_edit") : t("fac.modal_new")}</div>
                  <h2 className="text-3xl font-medium tracking-tight mt-1">
                    {form.id ? t("fac.update_title") : t("fac.create_title")}
                  </h2>
                </div>
                <button onClick={() => setShowModal(false)} className="text-zinc-400 hover:text-zinc-950"><X size={20} strokeWidth={1.5} /></button>
              </div>
              <form onSubmit={submit} className="space-y-4" data-testid="facultad-form">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("fac.field_nombre")}</label>
                  <input required value={form.nombre} onChange={set("nombre")} className="w-full border border-zinc-300 px-4 py-3" data-testid="fac-nombre" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("fac.field_codigo")}</label>
                  <input required value={form.codigo} onChange={set("codigo")} placeholder={t("fac.field_codigo_ph")} className="w-full border border-zinc-300 px-4 py-3" data-testid="fac-codigo" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{t("fac.field_contacto")}</label>
                  <input value={form.contacto_coordinador} onChange={set("contacto_coordinador")} placeholder={t("fac.field_contacto_ph")} className="w-full border border-zinc-300 px-4 py-3" data-testid="fac-contacto" />
                </div>
                <div className="flex gap-3 pt-3">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50">{t("common.cancel")}</button>
                  <button type="submit" disabled={saving} className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-50" data-testid="fac-submit">
                    {saving ? t("fac.saving") : form.id ? t("fac.update") : t("fac.create")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
